'use client';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, ZoomIn, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';

export interface GalleryItem {
  type: 'image' | 'video';
  src?: string;
  alt?: string;
  caption?: string;
  videoId?: string;
  videoTitle?: string;
}

interface BookGalleryProps {
  items: GalleryItem[];
  bookTitle: string;
}

export default function BookGallery({ items, bookTitle }: BookGalleryProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [zoomOpen, setZoomOpen] = useState(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const activeItem = items[activeIndex];

  const openZoom = useCallback(() => {
    if (activeItem.type === 'image') {
      setZoomOpen(true);
    }
  }, [activeItem]);

  const closeZoom = useCallback(() => {
    setZoomOpen(false);
    // Return focus to trigger
    setTimeout(() => triggerRef.current?.focus(), 50);
  }, []);

  useEffect(() => {
    if (zoomOpen) {
      closeButtonRef.current?.focus();
    }
  }, [zoomOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && zoomOpen) {
        closeZoom();
      }
      if (e.key === 'ArrowLeft' && !zoomOpen) {
        setActiveIndex((i) => (i > 0 ? i - 1 : items.length - 1));
      }
      if (e.key === 'ArrowRight' && !zoomOpen) {
        setActiveIndex((i) => (i < items.length - 1 ? i + 1 : 0));
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [zoomOpen, closeZoom, items.length]);

  // Trap focus in zoom dialog
  useEffect(() => {
    if (!zoomOpen || !dialogRef.current) return;
    const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabIndex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const trap = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last?.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener('keydown', trap);
    return () => document.removeEventListener('keydown', trap);
  }, [zoomOpen]);

  if (!items.length) return null;

  return (
    <div className="flex flex-col gap-4">
      {/* Main display — premium frame */}
      <div
        className="relative flex items-center justify-center"
        style={{
          background: 'linear-gradient(145deg, #F6F7F3 0%, #E9EEEA 100%)',
          border: '1px solid rgba(10, 76, 100, 0.10)',
          borderRadius: '18px',
          padding: '48px 36px',
          minHeight: '320px',
          boxShadow: '0 8px 32px rgba(7, 25, 35, 0.08), 0 2px 8px rgba(7, 25, 35, 0.04)'
        }}
      >
        {activeItem.type === 'image' ? (
          <>
            <AppImage
              src={activeItem.src || '/assets/images/no_image.png'}
              alt={activeItem.alt || `Visuel du livre ${bookTitle}`}
              width={325}
              height={461}
              className="w-auto mx-auto"
              style={{
                objectFit: 'contain',
                objectPosition: 'center',
                maxWidth: '320px',
                maxHeight: '380px',
                width: 'auto',
                height: 'auto',
                borderRadius: '2px',
                boxShadow: '0 18px 36px rgba(7, 25, 35, 0.14), 0 4px 10px rgba(7, 25, 35, 0.08)'
              }}
            />
            <button
              ref={triggerRef}
              onClick={openZoom}
              className="absolute bottom-3 right-3 bg-white/90 hover:bg-white text-[#071923] rounded-lg p-2 shadow-sm transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A4C64] min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label={`Agrandir l'image : ${activeItem.alt || 'visuel du livre'}`}
            >
              <ZoomIn size={18} aria-hidden="true" />
            </button>
          </>
        ) : (
          <div className="w-full flex flex-col items-center justify-center gap-3 py-10 px-6 text-center">
            <div className="w-16 h-16 rounded-full bg-[#071923]/10 flex items-center justify-center">
              <Play size={28} className="text-[#071923] ml-1" aria-hidden="true" />
            </div>
            <p className="text-sm font-semibold text-[#071923]">{activeItem.videoTitle}</p>
            <p className="text-xs text-[#536873]">Vidéo disponible dans la section ci-dessous</p>
          </div>
        )}
      </div>

      {/* Navigation arrows (keyboard) */}
      {items.length > 1 && (
        <div className="flex items-center justify-between px-1">
          <button
            onClick={() => setActiveIndex((i) => (i > 0 ? i - 1 : items.length - 1))}
            className="flex items-center gap-1 text-xs text-[#536873] hover:text-[#071923] transition-colors min-h-[44px] min-w-[44px] justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A4C64] rounded"
            aria-label="Visuel précédent"
          >
            <ChevronLeft size={16} aria-hidden="true" />
            <span className="sr-only">Précédent</span>
          </button>
          <span className="text-xs text-[#536873]" aria-live="polite" aria-atomic="true">
            {activeIndex + 1} / {items.length}
          </span>
          <button
            onClick={() => setActiveIndex((i) => (i < items.length - 1 ? i + 1 : 0))}
            className="flex items-center gap-1 text-xs text-[#536873] hover:text-[#071923] transition-colors min-h-[44px] min-w-[44px] justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A4C64] rounded"
            aria-label="Visuel suivant"
          >
            <span className="sr-only">Suivant</span>
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        </div>
      )}

      {/* Thumbnails */}
      {items.length > 1 && (
        <div
          className="flex gap-2 overflow-x-auto pb-1"
          role="list"
          aria-label={`Miniatures des visuels du livre ${bookTitle}`}
        >
          {items.map((item, idx) => (
            <button
              key={`thumb-${idx}`}
              onClick={() => setActiveIndex(idx)}
              role="listitem"
              className={`flex-shrink-0 w-16 h-20 rounded-lg overflow-hidden border-2 transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A4C64] focus-visible:ring-offset-1 ${
                idx === activeIndex
                  ? 'border-[#D1A338] shadow-sm'
                  : 'border-transparent hover:border-[#0A4C64]/30'
              }`}
              aria-label={`Voir ${item.type === 'video' ? 'la vidéo' : item.alt || `visuel ${idx + 1}`}`}
              aria-pressed={idx === activeIndex}
            >
              {item.type === 'image' ? (
                <AppImage
                  src={item.src || '/assets/images/no_image.png'}
                  alt=""
                  width={64}
                  height={80}
                  className="w-full h-full object-contain bg-[#F5F3EF]"
                />
              ) : (
                <div className="w-full h-full bg-[#071923]/10 flex items-center justify-center">
                  <Play size={16} className="text-[#071923]" aria-hidden="true" />
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Caption */}
      {activeItem.caption && (
        <p className="text-xs text-[#536873] text-center italic">{activeItem.caption}</p>
      )}

      <p className="text-xs text-[#536873] text-center">Visuels du livre fournis par APTE et l&apos;éditeur.</p>

      {/* Zoom modal */}
      {zoomOpen && activeItem.type === 'image' && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label={`Zoom : ${activeItem.alt || 'visuel du livre'}`}
          ref={dialogRef}
          onClick={(e) => { if (e.target === e.currentTarget) closeZoom(); }}
        >
          <div className="relative max-w-2xl w-full bg-white rounded-2xl overflow-hidden shadow-2xl">
            <button
              ref={closeButtonRef}
              onClick={closeZoom}
              className="absolute top-3 right-3 z-10 bg-white/90 hover:bg-white text-[#071923] rounded-lg p-2 shadow-sm transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0A4C64] min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Fermer le zoom (Échap)"
            >
              <X size={20} aria-hidden="true" />
            </button>
            <div className="bg-[#F5F3EF] p-6 flex items-center justify-center" style={{ minHeight: 400 }}>
              <AppImage
                src={activeItem.src || '/assets/images/no_image.png'}
                alt={activeItem.alt || `Visuel du livre ${bookTitle}`}
                width={480}
                height={640}
                className="object-contain max-h-[70vh] w-auto mx-auto"
                style={{ objectFit: 'contain' }}
              />
            </div>
            {activeItem.alt && (
              <div className="px-6 py-3 border-t border-[#E2E8EC]">
                <p className="text-xs text-[#536873] text-center">{activeItem.alt}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
